import { PointStatus } from '@luzula/luzula-lib';

/** Ein Projekt-Eintrag in `header.projects`. */
export interface ProjectRef {
	id: string;
	name: string;
}

/**
 * Gehört der Punkt zum Projekt? Ja, wenn `header.projects` es nennt oder
 * `workflow.projectId` darauf zeigt (Altpunkte aus Importen vor Juli 2026 tragen
 * nur Letzteres).
 *
 * Daran hängt der Gruppen-Stempel (30.09.2026, PERMISSIONS.md §13): Nur ein
 * zugehöriger Punkt bekommt beim Speichern die Gruppen des aktiven Projekts, und
 * nur zugehörige werden bei einer Gruppen-Zuordnung nachgestempelt.
 */
export function gehoertZuProjekt(punkt: any, projektId: string | null | undefined): boolean {
	if (!punkt || !projektId) return false;
	const projekte = punkt.header?.projects;
	if (Array.isArray(projekte) && projekte.some((p: any) => p?.id === projektId)) return true;
	return punkt.workflow?.projectId === projektId;
}

/**
 * Liefert das Projekt, dem ein Punkt zugeordnet ist, **wenn** es ein ANDERES als
 * das aktive Projekt ist — sonst `null`. Grundlage für den Cross-Projekt-Hinweis
 * und das read-only-Schalten des Workflow-Diagramms (Einfachmodell: ein Punkt
 * gehört zu genau einem Projekt; von einem fremden Projekt aus ist sein Workflow
 * nicht steuerbar).
 *
 * `null` (kein Hinweis) bei: kein aktives Projekt, Punkt ohne Projekt
 * (Bestand → Übernahme greift), oder Punkt IST Mitglied des aktiven Projekts.
 */
export function foreignProjectOf(
	projects: ReadonlyArray<ProjectRef> | undefined | null,
	activeProjectId: string | null | undefined
): ProjectRef | null {
	if (!activeProjectId) return null;
	const list = projects ?? [];
	if (list.length === 0) return null;
	if (list.some(p => p?.id === activeProjectId)) return null;
	return list[0] ?? null;
}

/**
 * Soll ein Punkt in Karte/Liste AUSGEBLENDET werden, weil er ein laufender
 * (WIP-)Punkt eines ANDEREN Projekts ist?
 *
 * Gegenstück zum Lib-Cross-Projekt-Gate (`WorkflowService.canSeePoint`), das nur
 * `workflow.projectId` kennt. Frisch angelegte Punkte tragen aber (noch) keine
 * `workflow.projectId`, sondern nur die App-eigene Mitgliedschaft
 * `header.projects` — die kennt nur die App, also gatet sie hier (so wie der
 * Lib-Kommentar zu `belongsToOtherProject` es vorsieht). Damit greift der Schutz
 * auch für Punkte ohne `workflow.projectId`.
 *
 * Sichtbar bleiben: abgeschlossene Punkte + Bestand (öffentlich), Punkte des
 * aktiven Projekts, Punkte ohne Projekt — und ALLES für den Entwickler
 * (voller Sicht-Bypass, User-Entscheidung 2026-05-26).
 */
export function isForeignWipHidden(opts: {
	projects: ReadonlyArray<ProjectRef> | undefined | null;
	status: PointStatus | null;
	activeProjectId: string | null | undefined;
	isDeveloper: boolean;
	/**
	 * `workflow.projectId` des Punkts (Bug-2-Fix 2026-06-18): Zeigt der Punkt-
	 * Workflow auf das aktive Projekt, gehört der Punkt definitiv dazu — auch
	 * wenn `header.projects` (App-Mitgliedschaft) noch nicht nachgezogen ist
	 * (z.B. nach Reaktivierung oder einem Sync-Konflikt-Merge, der nur den
	 * `workflow`-Block übernahm). Symmetrisch zum Lib-Tor `belongsToOtherProject`,
	 * das ebenfalls `workflow.projectId` als Quelle der Wahrheit nutzt.
	 */
	workflowProjectId?: string | null;
}): boolean {
	if (opts.isDeveloper) return false;
	// Bestand (null) und abgeschlossen sind öffentlich — nie ausblenden.
	if (opts.status === null || opts.status === 'abgeschlossen') return false;
	// Bug-2-Fix: Gehört der Punkt-Workflow zum aktiven Projekt, ist er NIE fremd —
	// unabhängig vom (evtl. veralteten) header.projects.
	if (opts.workflowProjectId && opts.activeProjectId
		&& opts.workflowProjectId === opts.activeProjectId) {
		return false;
	}
	// WIP-Status (bearbeitung/kontrolle/in_pruefung/abgelehnt): nur sichtbar,
	// wenn der Punkt zum aktiven Projekt gehört (oder gar keins zugeordnet ist).
	return foreignProjectOf(opts.projects, opts.activeProjectId) !== null;
}
